import { useState } from "react";
import { useNavigate } from "react-router-dom";

import LargePopupRedirect from "../CommonPopups/CommonLargePopupRedirect";
import UsersSettings from "../../../containers/Settings/Users";

import IconTearDropButton from "../CommonButtons/IconTearDropButton";
import PrimaryWithIconButton from "../CommonButtons/PrimaryWithIconButton";
import TitleSubtitlePair from "../TitleSubtitlePair";

import ICONS from "../../../assets/Icons";

/**
 * Reusable component for linking entity/user details.
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {Object} props.payload - Payload object containing entity details.
 * @param {Function} props.setPayload - Function to update payload state.
 * @param {Object} props.selectedValue - Selected value used in popup flow.
 *
 * @param {string} [props.headerTitle="View All"] - Popup header title.
 * @param {string} [props.redirectPath="/"] - Redirect path for full view.
 * @param {string} [props.flow=""] - Flow name passed to user settings component.
 *
 * @param {string} [props.title="User"] - Card title.
 * @param {string} [props.description=""] - Card description.
 * @param {string} [props.buttonText="Select"] - Empty state button text.
 *
 * @param {string} [props.nameKey="userName"] - Payload key for entity name.
 * @param {string} [props.idKey="userId"] - Payload key for entity ID.
 *
 * @param {string} [props.nameLabel="User name"] - Label for entity name.
 * @param {string} [props.idLabel="User ID"] - Label for entity ID.
 * @param {boolean} [props.isOptional=false] - Indicates if the entity is optional.
 *
 * @returns {JSX.Element}
 */
const LinkEntityCard = ({
  payload,
  setPayload,
  selectedValue,
  headerTitle = "View All",
  redirectPath = "/",
  flow = "",
  title = "User",
  description = "",
  buttonText = "Select",
  nameKey = "userName",
  idKey = "userId",
  nameLabel = "User name",
  idLabel = "User ID",
  isOptional = false,
}) => {
  const navigate = useNavigate();

  const [isPopUpOpen, setIsPopUpOpen] = useState(false);

  const hasSelectedValue = Boolean(payload?.[nameKey]);

  return (
    <>
      {isPopUpOpen && (
        <LargePopupRedirect
          headerTitle={headerTitle}
          headerFontWeight="semibold"
          headerFontColor="text-primary"
          headertextSize="font-md"
          closeiconhandleFunction={() => setIsPopUpOpen(false)}
          isfullViewrequired={false}
          fullviewhandleFunction={() => navigate(redirectPath)}
          isscrollRequired={true}
        >
          <UsersSettings
            type="popup"
            setIsPopUpOpen={setIsPopUpOpen}
            setPayload={setPayload}
            selectedValueUser={selectedValue}
            flow={flow}
          />
        </LargePopupRedirect>
      )}

      <div className="flex flex-col relative w-width-full">
        <div className="p-padding-md bg-primary-card flex flex-col rounded-border-radius-md">
          <div className="mb-margin-md">
            <div className="text-text-primary text-font-md">
              <span className="font-semibold">{title}</span>
              {isOptional && <span className="font-normal"> (Optional)</span>}
            </div>

            {description && (
              <p className="text-text-secondary text-xs mt-margin-xs">
                {description}
              </p>
            )}
          </div>

          {hasSelectedValue && (
            <div className="top-top-base right-right-base absolute">
              <IconTearDropButton
                handleFunction={() => setIsPopUpOpen(true)}
                isDisabled={false}
                isLoading={false}
                enabledBgColor="var(--primary-nav-act)"
                icon={ICONS.EDIT}
                hoverOption="white"
                title="Edit"
              />
            </div>
          )}

          {hasSelectedValue ? (
            <div className="bg-primary-nav px-padding-md py-padding-sm rounded-border-radius-md">
              <div className="flex">
                <div className="w-width-md border-r-border border-primary-bg mr-margin-md pr-padding-md">
                  <TitleSubtitlePair
                    title={payload?.[nameKey]}
                    titleColor="text-primary"
                    subtitle={nameLabel}
                    isBold={true}
                    variant="sm"
                    enableTicker={true}
                  />
                </div>

                <TitleSubtitlePair
                  title={payload?.[idKey]}
                  titleColor="text-primary"
                  subtitle={idLabel}
                  isBold={true}
                  enableTicker={true}
                  variant="sm"
                />
              </div>
            </div>
          ) : (
            <PrimaryWithIconButton
              btnText={buttonText}
              enabledBgColor="var(--primary-nav)"
              isDisabled={false}
              handleFunction={() => setIsPopUpOpen(true)}
              iconToBeAlign="left"
              enabledTextColor="var(--text-primary)"
              icon={ICONS.EDIT_USERS_WITH_WHITE_CIRCLE}
              fullWidthRequired={true}
              hoverOption="white"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default LinkEntityCard;
